import {openModal} from 'actions/modalActions'
import {getWallet} from 'actions/walletActions'
import {
  RESET_ORDERS,
  SET_CURRENT_PAGE,
  SET_ORDERS_BY_PID_ERROR,
  SET_ORDERS_BY_PID_LOADED,
  SET_ORDERS_BY_PID_LOADING,
  SET_ORDERS_ERROR,
  SET_ORDERS_LOADED,
  SET_ORDERS_PAGING_LOADING,
  SET_ORDER_BY_OPS_ORDER_ID_ERROR,
  SET_ORDER_BY_OPS_ORDER_ID_LOADED,
  SET_ORDER_BY_OPS_ORDER_ID_LOADING,
  SET_ORDER_PAGINATION_HISTORY,
  SET_PER_PAGE
} from 'constants/actionTypes'
import {PURCHASED_PRODUCT} from 'constants/mixpanelConstants'
import {PAYMENT_METHOD_MODAL} from 'constants/modalsConstants'
import {CHARGE_AND_SAVE, PAY, REUSE_CARD_ON_FILE} from 'constants/paymentMethodConstants'
import {customerKeySelector, isInvoicePaySelector} from 'selectors/customerSelectors'
import {
  lastEvaluatedSelector,
  ordersPerPageSelector,
  pageHistorySelector
} from 'selectors/ordersSelectors'
import {userHasSavedPaymentMethodSelector} from 'selectors/walletSelectors'
import {opsApi, websocketUrl} from 'services/apis'
import {MixpanelEventData, trackMixpanelEvent} from 'services/mixpanel'
import {createGetUrlWithCustomerKey} from 'services/ordersServices'

// 3rd-party
import {Stripe, StripeCardNumberElement} from '@stripe/stripe-js'
import {TablePaginationDirection} from 'components/TablePagination/TablePagination'
import {CLEARPROP} from 'constants/productCardConstants'
import type {RootAction} from 'reducers'
import FetchApiError from 'services/FetchApiError'
import {AppDispatch, RootState} from 'services/store'
import {createQueryStringFromObject} from 'services/urlServices'
import type {
  CreateOrderRequestBody,
  CreateOrderResponse,
  CreatePaymentIntentRequestBody,
  CreatePaymentIntentResponse,
  GetOrderListResponse
} from 'types/orderTypes'
import {w3cwebsocket as W3CWebSocket} from 'websocket'

export function setInitialOrders(res: GetOrderListResponse, error: any) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: SET_ORDERS_LOADED,
      payload: res
    })
    dispatch({
      type: SET_ORDER_PAGINATION_HISTORY,
      payload: {
        page: 1
      }
    })
    if (error) {
      dispatch({
        type: SET_ORDERS_ERROR,
        payload: error
      })
    }
  }
}

export function getOrdersPaginated(direction?: TablePaginationDirection, currentPage = 1) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const ordersPerPage = ordersPerPageSelector(state)
    const customerKey = customerKeySelector(state)
    const {created, opsOrderId} =
      (direction === 'forward' ? lastEvaluatedSelector(state) : pageHistorySelector(state)) || {}
    let getUrl = `orders?index=recent&scanReverse=false&count=${ordersPerPage}`
    if (created && opsOrderId) {
      getUrl = `${getUrl}&${createQueryStringFromObject({
        lastOpsOrderId: opsOrderId,
        lastCreated: created
      })}`
    }

    getUrl = createGetUrlWithCustomerKey(getUrl, customerKey)

    dispatch({
      type: SET_ORDERS_PAGING_LOADING
    })

    return opsApi
      .get<GetOrderListResponse>(getUrl)
      .then(res => {
        dispatch({
          type: SET_ORDERS_LOADED,
          payload: res
        })
        dispatch({
          type: SET_ORDER_PAGINATION_HISTORY,
          payload: {
            page: currentPage,
            created,
            opsOrderId
          }
        })
      })
      .catch(err => {
        dispatch({
          type: SET_ORDERS_ERROR,
          payload: err
        })
      })
  }
}

export function setCurrentPage(pageNumber: number): RootAction {
  return {
    type: SET_CURRENT_PAGE,
    payload: pageNumber
  }
}

export function getOrdersForPid(pid: string) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const customerKey = customerKeySelector(state)

    dispatch({
      type: SET_ORDERS_BY_PID_LOADING
    })

    const getUrl = createGetUrlWithCustomerKey(`orders?index=PID&is=${pid}`, customerKey)

    return opsApi
      .get<GetOrderListResponse>(getUrl)
      .then(res => {
        dispatch({
          type: SET_ORDERS_BY_PID_LOADED,
          payload: res
        })
      })
      .catch(err => {
        dispatch({
          type: SET_ORDERS_BY_PID_ERROR,
          payload: err
        })
      })
  }
}

// This is a recursive function used to fetch until we have either all orders or the correct
// number of fulfilled orders. It fetches 10 orders at a time.

export function getOrderByOpsOrderId(opsOrderId?: string | null, productOrderId?: string | null) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const customerKey = customerKeySelector(state)
    const orderId = opsOrderId || productOrderId
    const indexKey = productOrderId ? 'productOrderId' : 'opsOrderId'

    dispatch({
      type: SET_ORDER_BY_OPS_ORDER_ID_LOADING
    })

    const getUrl = createGetUrlWithCustomerKey(
      `orders?index=${indexKey}&is=${orderId}`,
      customerKey
    )

    return opsApi
      .get<GetOrderListResponse>(getUrl)
      .then(res => {
        if (res.Items.length > 0) {
          dispatch({
            type: SET_ORDER_BY_OPS_ORDER_ID_LOADED,
            payload: res
          })
        } else {
          throw new FetchApiError(`can not find order by ${indexKey}: ${orderId}`)
        }
      })
      .catch(err => {
        dispatch({
          type: SET_ORDER_BY_OPS_ORDER_ID_ERROR,
          payload: err
        })
      })
  }
}

export function setOrdersTablePerPage(val: number): RootAction {
  localStorage.setItem('ordersPerPagePreference', String(val))
  return {
    type: SET_PER_PAGE,
    payload: val
  }
}

export function resetOrdersState(): RootAction {
  return {
    // @ts-ignore invalid type, no reducer is listening this
    type: RESET_ORDERS
  }
}

type PlaceOrderPropsType = {
  payload: CreateOrderRequestBody
  handleOrder: (order: CreateOrderResponse) => void
  handleOrderError: (error?: FetchApiError) => void
  mixpanelEventData: MixpanelEventData
}

export function createOrder({
  orderPayload,
  handleOrder,
  handleOrderError,
  handleOrderAbandoned,
  mixpanelEventData,
  orderPrice
}: Omit<PlaceOrderPropsType, 'payload'> & {
  orderPayload: PlaceOrderPropsType['payload']
  handleOrderAbandoned: () => void
  orderPrice?: number
}) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const isInvoicePay = isInvoicePaySelector(state)
    const customerKey = customerKeySelector(state)
    let order = orderPayload
    if (customerKey) {
      order = {
        ...orderPayload,
        orgKey: customerKey
      }
    }

    if (orderPayload.productType === CLEARPROP && isInvoicePay) {
      // It's possible that other users can be invoiced in the future that aren't part of
      // an org. With that being the case, we just want to pass in the payload to placeInvoicedOrder
      // since not all invoiced users will have an orgKey. We keep that logic out of placeInvoicedOrder.
      return placeInvoicedOrder({
        payload: order,
        handleOrder,
        handleOrderError,
        mixpanelEventData
      })
    } else {
      return placeNonInvoicedOrder({
        dispatch,
        state,
        payload: order,
        handleOrder,
        handleOrderError,
        handleOrderAbandoned,
        mixpanelEventData,
        orderPrice
      })
    }
  }
}
type PaymentDataType = {
  paymentPayload: CreatePaymentIntentRequestBody
  stripe: Stripe
  cardElement: StripeCardNumberElement
}

type OpsOrderWebSocket = {
  onOrderCreated: (data: any) => void
}

export function createOrderFromModal({
  orderPayload,
  paymentData,
  onOrderWebSocketMessage,
  onError: handleOrderError,
  mixpanelEventData
}: {
  orderPayload: PlaceOrderPropsType['payload']
  mixpanelEventData: PlaceOrderPropsType['mixpanelEventData']
  onError: (error?: FetchApiError) => void
  onOrderWebSocketMessage: OpsOrderWebSocket['onOrderCreated']
  paymentData: PaymentDataType
}) {
  return (dispatch: AppDispatch) => {
    const onSuccess = (order: CreateOrderResponse) => {
      handleMixpanelPurchasedProductEvent(mixpanelEventData, order)
      const {opsOrderId} = order

      // set up the web socket so that once the payment clears from Stripe, it will
      // call our web hook, and that will trigger a message to be sent on the web socket
      // we're listening to specific to the order.opsOrderId
      createOpsOrderWebSocket(opsOrderId!, onOrderWebSocketMessage, handleOrderError)

      // handle Stripe stuff
      return handleOrderPayment(dispatch, order, paymentData, handleOrderError)
    }

    const onError = (err?: FetchApiError) => {
      console.error('order creation failed', err)
      handleOrderError && handleOrderError(err)
    }

    return handleOrderPost(orderPayload, onSuccess, onError)
  }
}

function handleMixpanelPurchasedProductEvent(
  mixpanelEventData: MixpanelEventData,
  order: CreateOrderResponse
) {
  const {opsOrderId, productType} = order
  const payload = {
    ...mixpanelEventData,
    product: productType,
    orderId: opsOrderId
  }
  trackMixpanelEvent(PURCHASED_PRODUCT, payload)
}

function handleOrderPost(
  payload: CreateOrderRequestBody,
  onSuccess?: (order: CreateOrderResponse) => void,
  onError?: (error?: FetchApiError) => void
) {
  return opsApi
    .post<CreateOrderResponse>('orders', payload)
    .then(order => {
      if (onSuccess) {
        return onSuccess(order)
      }
    })
    .catch(err => {
      onError && onError(err)
    })
}

function createOpsOrderWebSocket(
  opsOrderId: string,
  onOrderCreated: OpsOrderWebSocket['onOrderCreated'],
  handleOrderError?: () => void
) {
  const ws = new W3CWebSocket(`${websocketUrl}?opsOrderId=${opsOrderId}`)
  // Set up timeout and after 30 seconds, if we still haven't received a message,
  // close the websocket and treat it as an order error
  const timer = setTimeout(() => {
    // close the web socket connection
    ws && ws.close()
    handleOrderError && handleOrderError()
  }, 30000)

  ws.onmessage = evt => {
    const orderObject = JSON.parse(evt.data as string)
    onOrderCreated(orderObject)
    clearTimeout(timer)
    // close the connection
    ws && ws.close()
  }
}

// This will get a payment intent based on the order.opsOrderId, then
// it will call stripe.confirmCardPayment passing in the necessary info
// needed to make a payment and tie it to the specific order.
function handleOrderPayment(
  dispatch: AppDispatch,
  order: CreateOrderResponse,
  paymentData: PaymentDataType,
  handleOrderError: (error?: any) => void
) {
  const {opsOrderId, productType} = order

  const {
    paymentPayload,
    paymentPayload: {cardAction, userName: name, userEmail: email},
    stripe,
    cardElement
  } = paymentData

  const payload: CreatePaymentIntentRequestBody = {
    ...paymentPayload,
    product: productType,
    opsOrderId
  }

  // we need to fetch the clientSecret from the BE based on the order
  return opsApi
    .post<CreatePaymentIntentResponse>('wallet', payload)
    .then(res => {
      const {clientSecret} = res
      if (clientSecret) {
        return stripe
          .confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                email,
                name
              }
            }
          })
          .then(result => {
            if (result.error) {
              handleOrderError(result.error)
            } else {
              if (cardAction === CHARGE_AND_SAVE) {
                dispatch(getWallet())
              }
            }
          })
          .catch((err: FetchApiError) => {
            console.error('stripe.confirmCardPayment error', err)
            handleOrderError(err)
          })
      }
    })
    .catch(err => {
      console.error('clientSecret error', err)
      handleOrderError(err)
    })
}

function handlePayWithSavedCardSubmit(
  {opsOrderId, productType}: CreateOrderResponse,
  handleOrderError: (error?: FetchApiError) => void,
  orderPrice: number | undefined
) {
  const payload = {
    cardAction: REUSE_CARD_ON_FILE,
    product: productType,
    opsOrderId,
    ...(orderPrice &&
      (productType === 'CDA' || productType === 'CDA_MLS') && {
        paymentAmount: Math.round(orderPrice * 100)
      })
  }
  // At this point, if the call is successful, there is nothing to do. The web socket
  // connection above will handle the new order when it's ready. We only need to worry
  // about failed payments here (saved card on file has expired, insufficient funds, etc)
  return opsApi.post<CreatePaymentIntentResponse>('wallet', payload).catch(err => {
    handleOrderError(err)
  })
}

function placeInvoicedOrder({
  payload,
  handleOrder,
  handleOrderError,
  mixpanelEventData
}: PlaceOrderPropsType) {
  const handleOrderCb = (res: CreateOrderResponse) => {
    handleMixpanelPurchasedProductEvent(mixpanelEventData, res)
    handleOrder(res)
  }

  const handleOrderErrorCb: (error?: FetchApiError) => void = err => {
    console.error('invoiced order creation failed', err)
    handleOrderError(err)
  }

  return placeOrder({
    payload,
    handleOrder: handleOrderCb,
    handleOrderError: handleOrderErrorCb
  })
}

function placeOrder({
  payload,
  handleOrder,
  handleOrderError
}: Omit<PlaceOrderPropsType, 'mixpanelEventData'>) {
  return opsApi
    .post<CreateOrderResponse>('orders', payload)
    .then(res => {
      handleOrder(res)
    })
    .catch(err => {
      handleOrderError(err)
    })
}

const placeNonInvoicedOrder = async ({
  dispatch,
  state,
  payload,
  handleOrder,
  handleOrderError,
  handleOrderAbandoned,
  mixpanelEventData,
  orderPrice
}: PlaceOrderPropsType & {
  dispatch: AppDispatch
  state: RootState
  handleOrderAbandoned: () => void
  orderPrice?: number
}) => {
  try {
    const userHasSavedPaymentMethod = userHasSavedPaymentMethodSelector(state)
    // use user's saved card if it exists, else open payment modal
    if (userHasSavedPaymentMethod) {
      const onSuccess = (order: CreateOrderResponse) => {
        handleMixpanelPurchasedProductEvent(mixpanelEventData, order)
        const {opsOrderId} = order
        // set up the web socket so that once the payment clears from Stripe, it will
        // call our web hook, and that will trigger a message to be send on the web socket
        // we're listening to specific to the order.opsOrderId
        createOpsOrderWebSocket(opsOrderId!, handleOrder, handleOrderError)

        return handlePayWithSavedCardSubmit(order, handleOrderError, orderPrice)
      }
      const onError: (error?: FetchApiError) => void = err => {
        console.error('saved card order creation failed', err)
        handleOrderError(err)
      }
      return handleOrderPost(payload, onSuccess, onError)
    } else {
      // open PAYMENT_METHOD_MODAL, only create order once user submits from the modal.
      // The modal will then call createOrderFromModal passing in the orderPayload and
      // using the handleOrder function as part of its onOrderWebSocketMessage callback
      dispatch(
        openModal({
          modalType: PAYMENT_METHOD_MODAL,
          mode: PAY,
          orderPayload: payload,
          handleOrder,
          handleOrderError,
          handleExited: handleOrderAbandoned,
          mixpanelEventData,
          orderPrice
        })
      )
    }
  } catch (err) {
    console.error(err)
  }
}
